import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import Map from '@rio-cloud/rio-uikit/Map';
import MapSettings from '@rio-cloud/rio-uikit/MapSettings';
import MapTypeSettings from '@rio-cloud/rio-uikit/MapTypeSettings';
import type { MapApi, MapEvent } from '@rio-cloud/rio-uikit/mapTypes';
import { Fragment, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type ConnectedProps, connect } from 'react-redux';

import { config } from '../../config';
import type { Dispatch, State } from '../../types';
import { setMapCenter, setMapZoom, updateMarkerPosition } from '../actions/mapActions';
import { getMapBoundingBox, getMapCenter, getZoom } from '../reducers/selectors';
import type { Coordinates } from '../types';
import MapGeofenceRenderer from './MapGeofenceRenderer';
import MapPoiRenderer from './MapPoiRenderer';

const resizeMap = (api: MapApi) => {
    // TODO: check performance fo unnecessary resize on every render - solution might be to check
    // whether or not the AssetTree and/or the Asset details sidebar are shown
    api.map.getViewPort().resize();
};

const MapComponent = (props: Props) => {
    const mapApi = useRef<MapApi>();

    const { center, zoom, setZoom, setCenter, boundingBox, updateMarkerPosition } = props;

    const intl = useIntl();

    const hereCredentials = { apikey: config.hereApiKey };

    const eventListenerMap = {
        [EventUtils.TAP]: (event: MapEvent) => {
            const pointer = event.currentPointer;
            updateMarkerPosition(mapApi.current?.map.screenToGeo(pointer.viewportX, pointer.viewportY));
        },
        [EventUtils.MAP_VIEW_CHANGE_END]: (event: Event) => {
            const target: any = event.currentTarget;

            const updatedCenter = target.getCenter();
            const newZoom = target.getZoom();

            setCenter({
                latitude: updatedCenter.lat,
                longitude: updatedCenter.lng,
            });
            setZoom(newZoom);
        },
    };

    const markerEventListenerMap = {
        [EventUtils.DRAG_START]: (event: MapEvent) => {
            const target = event.target;
            const pointer = event.currentPointer;

            if (target?.draggable) {
                const targetPosition = mapApi.current?.map.geoToScreen(target.getGeometry());

                // @ts-expect-error // ignoring import of Here Maps JS API types
                target.offset = new H.math.Point(
                    pointer.viewportX - targetPosition.x,
                    pointer.viewportY - targetPosition.y
                );
                mapApi.current?.behavior.disable();
            }
        },
        [EventUtils.DRAG_END]: (event: MapEvent) => {
            const target = event.target;
            mapApi.current?.behavior.enable();

            const pointer = event.currentPointer;
            const coords = mapApi.current?.map.screenToGeo(
                pointer.viewportX - target.offset.x,
                pointer.viewportY - target.offset.y
            );
            updateMarkerPosition(coords);
        },
        [EventUtils.DRAG]: (event: MapEvent) => {
            const target = event.target;
            const pointer = event.currentPointer;
            if (target.draggable) {
                target.setGeometry(
                    mapApi.current?.map.screenToGeo(
                        pointer.viewportX - target.offset.x,
                        pointer.viewportY - target.offset.y
                    )
                );
            }
        },
    };

    return (
        <Map
            credentials={hereCredentials}
            center={center}
            zoom={zoom}
            boundingBox={boundingBox}
            eventListenerMap={eventListenerMap}
            mapSettings={
                <MapSettings
                    options={[
                        <MapTypeSettings
                            key='mapTypeSettings'
                            tooltip={intl.formatMessage({
                                id: 'intl-msg:common-message.component.map.changeMapTypeTooltip',
                            })}
                            dropdownHeaderText={
                                <FormattedMessage id='intl-msg:common-message.component.map.mapViews' />
                            }
                            defaultTypeLabel={
                                <FormattedMessage id='intl-msg:common-message.component.map.defaultView' />
                            }
                            truckTypeLabel={<FormattedMessage id='intl-msg:common-message.component.map.truckView' />}
                            terrainTypeLabel={
                                <FormattedMessage id='intl-msg:common-message.component.map.terrainView' />
                            }
                            satelliteTypeLabel={
                                <FormattedMessage id='intl-msg:common-message.component.map.satelliteView' />
                            }
                            nightTypeLabel={<FormattedMessage id='intl-msg:common-message.component.map.nightView' />}
                        />,
                    ]}
                />
            }
        >
            {(api: MapApi) => {
                mapApi.current = api;
                resizeMap(api);

                return (
                    <Fragment>
                        <MapPoiRenderer eventListenerMap={markerEventListenerMap} />
                        <MapGeofenceRenderer mapApi={api} markerEventListenerMap={markerEventListenerMap} />
                    </Fragment>
                );
            }}
        </Map>
    );
};

type Props = ConnectedProps<typeof connector>;

const mapStateToProps = (state: State) => ({
    center: getMapCenter(state),
    zoom: getZoom(state),
    boundingBox: getMapBoundingBox(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setZoom: (zoom: number) => dispatch(setMapZoom(zoom)),
    setCenter: (center: { latitude: number; longitude: number }) => dispatch(setMapCenter(center)),
    updateMarkerPosition: (coordinates?: Coordinates) => dispatch(updateMarkerPosition(coordinates)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
const MapWrapper = connector(MapComponent);
export default MapWrapper;
